/**
 * PageEntrance — Fade-in + slide-up wrapper for page content.
 *
 * Wrap page content in this for a subtle entrance animation on route change.
 *
 * @see documents/ui-enhancement-spec.md §9.3 (Page & Step Transitions)
 */

import React, { useEffect, useState } from 'react';
import { useZeliumTheme } from '../../theme';

interface PageEntranceProps {
  children: React.ReactNode;
  /** Optional delay in ms before entrance */
  delay?: number;
}

export function PageEntrance({ children, delay = 0 }: PageEntranceProps): React.ReactElement {
  const { reducedMotion, motion } = useZeliumTheme();
  const [visible, setVisible] = useState(reducedMotion);

  useEffect(() => {
    if (reducedMotion) {
      setVisible(true);
      return;
    }
    const timeout = setTimeout(() => setVisible(true), delay);
    return () => clearTimeout(timeout);
  }, [delay, reducedMotion]);

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(8px)',
    transition: `opacity ${motion.duration.normal}s ${motion.ease.enter}, transform ${motion.duration.normal}s ${motion.ease.enter}`,
  };

  return <div style={style}>{children}</div>;
}
